import React, { useState } from 'react';
import { RolesApi } from '@api';
import { RolesType } from '@types';
import { z } from 'zod';
import { FormInput, FormController, formSchemas } from '@components/form';
import { Text, VStack, Card } from '@chakra-ui/react';
import { OverlayDialog, toasterApi } from '@ui';

const formSchema = z.object({
  name: formSchemas.roleSchema.nameSchema,
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  role: RolesType.Role;
  setRole: (role: RolesType.Role) => void;
}

const CreateCard: React.FC<Props> = ({ role, setRole }) => {
  const [postRole] = RolesApi.Api.usePostRoleMutation();

  const canEdit = false;

  const onFormSubmit = async (formData: FormData) => {
    OverlayDialog.open('confirmRole', {
      overlayId: 'confirmRole',
      title: 'Confirm Role Creation',
      description: 'Are you sure you want to create the role?',
      content: (
        <VStack align="flex-start" gap={2}>
          <Text>This will create the role.</Text>
          <Text>
            Role Name:{' '}
            {formData.name === role.name
              ? role.name
              : `${role.name} -> ${formData.name}`}
          </Text>
        </VStack>
      ),
      handleConfirm: async () => {
        try {
          const data = await postRole({
            name: formData.name,
            permissions: [],
          }).unwrap();

          setRole?.(data as RolesType.Role);

          toasterApi({
            type: 'success',
            message: 'Role created successfully',
          });
        } catch (err: any) {
          toasterApi({
            type: 'error',
            message:
              err?.data?.detail ||
              err?.data?.message ||
              'Failed to create role',
          });
        }
      },
    });
  };

  return (
    <Card.Root width="100%" variant="elevated">
      <Card.Body gap="7">
        <FormController
          key={role.id}
          label={'Create Role'}
          formSchema={formSchema}
          helperText={
            canEdit ? '' : 'Please fill out the form to create a new role.'
          }
          onFormSubmit={onFormSubmit}
          disabled={canEdit}
          defaultValues={{
            name: role?.name,
          }}
          buttonProps={{
            variant: 'outline',
            color: 'tertiary.fg',
            width: '100%',
            mt: '4',
          }}
        >
          {({ register, control, errors }) => {
            return (
              <FormInput
                control={control}
                disabled={canEdit}
                label="Role Name"
                id="name"
                register={register}
                errors={errors}
                helperText={formSchemas.roleSchema.roleHelper}
              />
            );
          }}
        </FormController>
      </Card.Body>
    </Card.Root>
  );
};

export default CreateCard;